/*
 * Modal de Materialize, con la misma API que exponia ngx-materialize: mz-modal con
 * [fixedFooter] y [fullscreen], los tres bloques de contenido, openModal()/closeModal()
 * y la clase base MzBaseModal con la propiedad modalComponent.
 *
 * Las 220 etiquetas de modal del proyecto se apoyan en esta forma exacta.
 */
import { Component, Directive, Input, OnDestroy, ViewEncapsulation, inject, ElementRef } from '@angular/core';

/** Opciones del modal de Materialize 0.100, tal como las declara el proyecto. */
export interface ModalOptions {
	dismissible?: boolean;
	opacity?: number;
	inDuration?: number;
	outDuration?: number;
	startingTop?: string;
	endingTop?: string;
	ready?: () => void;
	complete?: () => void;
}

declare const $: ((el: unknown) => {
	modal?: (opciones?: ModalOptions | string) => void;
}) | undefined;

/**
 * Lo unico que el proyecto le pide a `modalComponent`: cerrar. Hay 27 llamadas a
 * `this.modalComponent.closeModal()` y ninguna a nada mas.
 *
 * Existe como interfaz, y no como la clase del componente, porque MzModalService abre
 * los modales de forma dinamica y necesita entregar un controlador sin instanciar el
 * componente. Instanciarlo con `new` es justo lo que fallaba: MzModalComponent resuelve
 * su ElementRef con inject() en un inicializador de campo, y fuera de un contexto de
 * inyeccion eso lanza NG0203.
 */
export interface MzModalControl {
	openModal(): void;
	closeModal(): void;
}

/**
 * Invoca el plugin de Materialize sobre el `.modal` que haya dentro de `raiz`.
 *
 * Compartida por el componente y por el controlador que entrega el servicio, para que
 * las dos vias se comporten igual, incluida la guarda de inicializacion.
 */
function invocaModal(raiz: Element | null, arg: ModalOptions | string): void {
	const el = raiz?.querySelector('.modal') ?? null;
	if (!el || typeof $ !== 'function') {
		return;
	}

	/*
	 * Los metodos por nombre ('open', 'close') no comprueban que el modal este
	 * inicializado. materialize.js hace, literalmente:
	 *
	 *     return this.each(function () { this.M_Modal[methodOrOptions](); });
	 *
	 * `M_Modal` solo existe despues de que Modal.init() haya corrido, es decir despues
	 * de un openModal(). Un modal que se pinto y nunca se abrio -que es lo normal: estan
	 * en la plantilla esperando- no lo tiene, y cerrarlo lanzaba:
	 *
	 *     TypeError: Cannot read properties of undefined (reading 'close')
	 *
	 * Pasar un objeto si es seguro: esa rama inicializa.
	 */
	if (typeof arg === 'string' && !(el as unknown as { M_Modal?: unknown }).M_Modal) {
		return;
	}

	const jq = $(el);
	if (typeof jq.modal === 'function') {
		jq.modal(arg);
	}
}

/**
 * Controlador de un modal que ya esta en el DOM, con la misma superficie que el
 * componente. Lo usa MzModalService para los modales que abre dinamicamente.
 */
export function controlaModal(raiz: Element, opciones?: ModalOptions): MzModalControl {
	return {
		openModal(): void {
			invocaModal(raiz, opciones ?? {});
			invocaModal(raiz, 'open');
		},
		closeModal(): void {
			invocaModal(raiz, 'close');
		},
	};
}

@Component({
	selector: 'mz-modal',
	encapsulation: ViewEncapsulation.None,
	template: `
		<div class="modal" [class.modal-fixed-footer]="fixedFooter" [class.bottom-sheet]="bottomSheet"
				[style.width]="fullscreen ? '95%' : null" [style.max-height]="fullscreen ? '95%' : null">
			<ng-content />
		</div>
	`,
})
export class MzModalComponent implements MzModalControl, OnDestroy {
	@Input() fixedFooter = false;
	@Input() fullscreen = false;
	@Input() bottomSheet = false;
	@Input() options?: ModalOptions;

	private readonly host = inject(ElementRef<HTMLElement>);

	private get raiz(): HTMLElement {
		return this.host.nativeElement as HTMLElement;
	}

	openModal(): void {
		invocaModal(this.raiz, this.options ?? {});
		invocaModal(this.raiz, 'open');
	}

	closeModal(): void {
		invocaModal(this.raiz, 'close');
	}

	ngOnDestroy(): void {
		this.closeModal();
	}
}

@Component({
	selector: 'mz-modal-header',
	encapsulation: ViewEncapsulation.None,
	template: `<div class="modal-header"><ng-content /></div>`,
})
export class MzModalHeaderComponent {}

@Component({
	selector: 'mz-modal-content',
	encapsulation: ViewEncapsulation.None,
	template: `<div class="modal-content"><ng-content /></div>`,
})
export class MzModalContentComponent {}

@Component({
	selector: 'mz-modal-footer',
	encapsulation: ViewEncapsulation.None,
	template: `<div class="modal-footer"><ng-content /></div>`,
})
export class MzModalFooterComponent {}

/**
 * Clase base de los componentes que se abren como modal. ngx-materialize la usaba para
 * dar acceso a modalComponent; los 13 componentes modaledit y deletemodal del proyecto
 * la extienden y llaman a this.modalComponent.closeModal().
 */
export abstract class MzBaseModal {
	/** Lo inyecta el servicio que abre el modal. */
	modalComponent!: MzModalControl;
}

/**
 * [mz-modal-close]: 66 usos en 21 plantillas del proyecto, sobre los botones CANCELAR y
 * ACEPTAR de cada modal.
 *
 * Materialize cierra el modal al hacer clic en cualquier descendiente con la clase
 * .modal-close; ngx-materialize exponia esta directiva solo para ponerla. Falto en los
 * tres primeros spikes y NO lo detecta el compilador: es un atributo plano, sin
 * corchetes, sobre un <button>, y Angular solo rechaza elementos desconocidos y
 * propiedades ligadas desconocidas. El build quedaba verde y los botones no cerraban.
 */
@Directive({
	selector: '[mz-modal-close]',
	host: { class: 'modal-close' },
})
export class MzModalCloseDirective {}

export const MZ_MODAL = [
	MzModalComponent,
	MzModalHeaderComponent,
	MzModalContentComponent,
	MzModalFooterComponent,
	MzModalCloseDirective,
] as const;
